<template>
  <div
    v-if="campaignId && campaignEventId && $auth.isAuthenticated && getReady"
    class="campaign-event-detail-wrapper d-flex flex-column"
  >
    <h4 class="campaign-event-detail-title">
      {{ campaignEventTitle }}
    </h4>
    <div
      class="campaign-event-detail"
      :class="{ 'optimized-layout': !isDesktop }"
    >
      <div class="campaign-event-detail--graphs">
        <b-card class="campaign-event-detail--graphs-graph">
          <SubscriberLanguageGraph
            v-if="
              campaignGraphData &&
              campaignGraphData.subscriberLanguageData &&
              textDialects &&
              textDialects.length > 0
            "
            :graphData="campaignGraphData.subscriberLanguageData"
            :dialects="textDialects"
          />
        </b-card>
        <b-card class="campaign-event-detail--graphs-graph">
          <DeliveryStatusGraph
            v-if="campaignGraphData && campaignGraphData.deliveryStatusData"
            :graphData="campaignGraphData.deliveryStatusData"
          />
        </b-card>
        <b-card class="campaign-event-detail--graphs-graph">
          <MessageSentGraph
            v-if="campaignGraphData && campaignGraphData.messageSentData"
            :graphData="campaignGraphData.messageSentData"
          />
        </b-card>
      </div>
      <div v-if="!isDesktop" class="campaign-event-detail--mobile-btns">
        <router-link
          :to="{
            name: 'Campaign Dashboard',
            params: {
              campaignId: campaignEventDetail.campaign_id,
            },
          }"
        >
          <b-button variant="info" pill>
            <b-icon-arrow-bar-left />
          </b-button>
        </router-link>
        <b-button variant="info" pill @click="toggleDetailInfo">
          <b-icon-info-lg v-if="!isDetailInfoOpened" />
          <b-icon-x-lg v-else />
        </b-button>
      </div>
      <div
        class="campaign-event-detail--info"
        :class="{ opened: detailInfoOpened, 'bg-light': !isDesktop }"
      >
        <b-card class="campaign-event-detail--info-card">
          <CampaignEventInfo
            v-if="campaignEventDetail"
            :event="campaignEventDetail"
          />
        </b-card>
        <b-card class="campaign-event-detail--info-card">
          <CampaignEventSubscribers
            v-if="campaignEventDetail"
            :eventId="campaignEventDetail.event_id"
            @updating="(value) => {updatingEventSubscribers = value;}"
          />
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import moment from "moment";
import DeliveryStatusGraph from "./DeliveryStatusGraph.vue";
import SubscriberLanguageGraph from "./SubscriberLanguageGraph.vue";
import MessageSentGraph from "./MessageSentGraph.vue";
import CampaignEventInfo from "./CampaignEventInfo.vue";
import CampaignEventSubscribers from "./CampaignEventSubscribers.vue";
import { runGraphqlQuery } from "../../graphql/utils";
import { makeGetCampaignStatsQuery } from "../../graphql/queries";
import { EventBus } from "@/events/eventBus";
import errorHandler from "@/mixins/errorHandler";

export default {
  name: "CampaignEventDetail",
  mixins: [errorHandler],
  components: {
    DeliveryStatusGraph,
    SubscriberLanguageGraph,
    MessageSentGraph,
    CampaignEventInfo,
    CampaignEventSubscribers,
  },
  data() {
    return {
      isDetailInfoOpened: false,
      isDesktop: true,
      isLoadingEventDetail: false,
      isLoadingGraphData: false,
      campaignGraphData: null,
      campaignEventDetail: null,
      campaignEventInterval: null,
      intervalTime: 2500,
      updatingEventSubscribers: false,
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("agency", ["currentAgencyId", "textDialects"]),
    ...mapGetters("user", ["currentToken", "viewport", "isDarkMode"]),
    campaignId() {
      return this.$route.params.campaignId;
    },
    campaignEventId() {
      return this.$route.params.campaignEventId;
    },
    campaignEventTitle() {
      if (this.campaignEventDetail) {
        return `Campaign Event (${this.campaignEventDetail.event_name})`;
      }
      return "Campaign Event";
    },
    detailInfoOpened() {
      if (this.isDesktop) {
        return true;
      } else {
        return this.isDetailInfoOpened;
      }
    },
    getReady() {
      return !this.isLoadingEventDetail && !this.isLoadingGraphData;
    },
    isInProgress() {
      return (!this.campaignEventDetail || (this.campaignEventDetail && this.campaignEventDetail.event_status === "pending"));
    },
  },
  created() {
    this.fetchOperations();

    this.campaignEventInterval = setInterval(async () => {
      if (this.getReady && !this.updatingEventSubscribers && this.isInProgress) {
        EventBus.$emit("update-campaign-event-detail-subscribers");
        this.fetchOperations(false);
      }
    }, this.intervalTime);
  },
  methods: {
    abortFetchOperations() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchOperations(showLoading = true) {
      this.fetchAbortController = new AbortController();
      await this.fetchCampaignEventDetail(showLoading);
      await this.fetchCampaignEventGraphData(showLoading);
    },
    toggleDetailInfo() {
      this.isDetailInfoOpened = !this.isDetailInfoOpened;
    },
    getStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    async fetchCampaignEventDetail(showLoading = true) {
      try {
        this.isLoadingEventDetail = showLoading;
        const resp = await this.$ConveyApi.campaignsApi.getCampaignEvent({
          campaignId: this.campaignId,
          eventId: this.campaignEventId,
          signal: this.fetchAbortController.signal,
        });
        if (resp.data.data && resp.data.data.length > 0) {
          this.campaignEventDetail = resp.data.data[0];
          const { event_status } = resp.data.data[0];
          if (event_status !== "pending" && this.campaignEventInterval) {
            clearTimeout(this.campaignEventInterval);
            this.campaignEventInterval = null;
          }
        }
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log(
            "[CampaignEventDetail:fetchCampaignEventDetail] ERROR: ",
            err
          );
        }
      } finally {
        this.isLoadingEventDetail = false;
      }
    },
    async fetchCampaignEventGraphData(showLoading = true) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.isLoadingGraphData = showLoading;
        const query = makeGetCampaignStatsQuery(this.campaignEventId);
        const response = await runGraphqlQuery(query);

        const {
          messageSentStats,
          messagesStats,
          messsageDeliveryStats
        } = response.data.data.getCampaign;
        messageSentStats.unshift({
          x_axis: moment.utc(this.campaignEventDetail.created_at).tz(tz).valueOf(),
          count: 0,
        });
        messageSentStats.push({
          x_axis: moment().valueOf(),
          count: 0,
        });
        const groupedMessageSentStats = messageSentStats
          .map((item, itemIndex) => {
            const { count, x_axis } = item;
            const timestamp = Math.ceil(Number(x_axis) / 1000) * 1000;
            return {
              time: itemIndex < messageSentStats.length - 1
                ? moment.utc(timestamp).tz(tz).format("hh:mm:ss")
                : "Now",
              count: Number(count),
            };
          })
          .reduce((grouped, item) => {
            const key = item.time;
            if (!grouped[key]) {
              grouped[key] = [];
            }

            grouped[key].push(item);

            return grouped;
          }, {});

        const formattedMessageSentStats = [];
        Object.keys(groupedMessageSentStats).forEach(key => {
          const time = key;
          const count = groupedMessageSentStats[key].reduce((a, b) => a + b.count || 0, 0);
          formattedMessageSentStats.push({ time, count });
        });

        this.campaignGraphData = {
          subscriberLanguageData: {
            total: (messagesStats || []).reduce(
              (accum, item) => accum + Number(item.message_count),
              0
            ),
            data: (messagesStats || []).map((item) => ({
              lang: item.language,
              count: Number(item.message_count),
            })),
          },
          deliveryStatusData: {
            total: (messsageDeliveryStats || []).reduce(
              (accum, item) => accum + Number(item.count),
              0
            ),
            data: (messsageDeliveryStats || []).map((item) => ({
              status: this.getStatus(item.status),
              count: Number(item.count),
            })),
          },
          messageSentData: {
            data: formattedMessageSentStats,
          },
        };
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log(
            "[CampaignEventDetail:fetchCampaignEventGraphData] ERROR: ",
            err
          );
        }
      } finally {
        this.isLoadingGraphData = false;
      }
    },
  },
  beforeDestroy() {
    this.abortFetchOperations();
    if (this.campaignEventInterval) {
      clearInterval(this.campaignEventInterval);
      this.campaignEventInterval = null;
    }
  },
  watch: {
    viewport: {
      handler(value) {
        const { innerWidth } = value;
        this.isDesktop = innerWidth > 1199;
      },
      deep: true,
      immediate: true,
    },
    isDarkMode(darkMode) {
      const temp = { ...this.campaignGraphData };
      this.campaignGraphData = null;
      setTimeout(() => {
        this.campaignGraphData = { ...temp };
      }, 100);
    },
  },
};
</script>

<style lang="scss">
.campaign-event-detail-wrapper {
  width: 100%;
  height: 100%;
  gap: 1rem;

  .campaign-event-detail-title {
    margin: 0;
    height: 25px;
    line-height: 25px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .campaign-event-detail {
    width: 100%;
    height: calc(100% - 25px - 1rem);
    display: grid;
    grid-template-columns: 1fr min(600px, max(400px, 33%));
    gap: 1rem;
    overflow-y: auto;

    &--info {
      opacity: 0;
      width: 100%;
      height: 100%;
      overflow-y: auto;
      transition: opacity ease-in-out 0.15s;
      display: flex;
      flex-direction: column;
      gap: 1rem;

      &.opened {
        opacity: 1;
      }

      &-card {
        border-radius: 1rem;
        margin-bottom: 0 !important;

        &:nth-child(1) {}

        &:nth-child(2) {
          flex: 1;
        }
      }
    }

    &--graphs {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      height: 100%;
      overflow-y: auto;

      &-graph {
        border-radius: 1rem;
        margin: 0 !important;
        &:nth-child(1) {
          width: calc(50% - 0.5rem);

          @media screen and (max-width: 1600px) {
            width: 100%;
          }
        }
        &:nth-child(2) {
          width: calc(50% - 0.5rem);

          @media screen and (max-width: 1600px) {
            width: 100%;
          }
        }
        &:nth-child(3) {
          width: 100%;
        }
      }
    }

    &.optimized-layout {
      position: relative;
      display: block;

      .campaign-event-detail--graphs {
        position: relative;
        z-index: 2;
      }

      .campaign-event-detail--info {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;

        &.opened {
          z-index: 3;
        }
      }

      .campaign-event-detail--mobile-btns {
        position: fixed;
        z-index: 4;
        top: 112px;
        right: 16px;
        display: flex;
        align-items: center;
        gap: 8px;

        button {
          background-color: rgba(24, 109, 222, 0.5) !important;
        }
      }
    }
  }
}
</style>
